import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ProjectDTO } from '@app/shared/services/dtos/project/project.dto';
import { LoaderService } from '@app/shared/services/shared/loader.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { CreateProjectDTO } from '@app/shared/types/models/project/dto/create-project-dto';
import { UpdateProjectDTO } from '@app/shared/types/models/project/dto/update-project-dto';
import { Project } from '@app/shared/types/models/project/project.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable, tap } from 'rxjs';
import { AppEvent } from '../types/models/notifications/app-event';
import { DataService } from './data-service';
import { ProjectFactory } from './factories/project.factory';

/**
 * ProjectsDataService - Project entities and per-company project id collections (active, archived, draft).
 *
 * When {@link ActiveCompanyService.activeCompanyId} is set, loads that company’s project lists (constructor `effect`).
 * Active project selection and persistence are owned by {@link ActiveProjectService}, not this service.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Project lists, {@link ActiveProjectService}, and project CRUD.
 *
 * Architecture:
 * - {@link ProjectsDataService}: Store and HTTP for projects
 * - {@link ActiveCompanyService}: Which company’s projects to load
 * - {@link ActiveProjectService}: Active project context for the shell
 */
@Injectable({ providedIn: 'root' })
export class ProjectsDataService extends DataService<ProjectDTO, Project> {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly projectsFactory: ProjectFactory = inject(ProjectFactory);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);

  private projects: DataStore<Project> = new DataStore<Project>();
  private companyProjects: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private archivedProjects: DataStore<Project> = new DataStore<Project>();
  private archivedCompanyProjects: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private draftProjects: DataStore<Project> = new DataStore<Project>();
  private draftCompanyProjects: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  public constructor() {
    super();
    effect(() => {
      const companyId = this.activeCompanyService.activeCompanyId();
      if (!companyId) {
        return;
      }
      untracked(() => {
        this.loadCompanyProjects(companyId);
        this.loadCompanyArchivedProjects(companyId);
        this.loadCompanyDraftProjects(companyId);
      });
    });
  }
  public getCompanyProjects(companyId: string): Signal<Project[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.companyProjects
          .get(companyId)()
          ?.ids?.map(id => this.projects.get(id)()) || []
    );
  }

  public getById(projectId: string): Signal<Project | undefined> {
    return computed(() => this.projects.get(projectId)());
  }

  public getCompanyArchivedProjects(companyId: string): Signal<Project[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.archivedCompanyProjects
          .get(companyId)()
          ?.ids?.map(id => this.archivedProjects.get(id)()) || []
    );
  }

  public getArchiveProjectById(projectId: string): Signal<Project | undefined> {
    return computed(() => this.archivedProjects.get(projectId)());
  }

  public getCompanyDraftProjects(companyId: string): Signal<Project[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.draftCompanyProjects
          .get(companyId)()
          ?.ids?.map(id => this.draftProjects.get(id)()) || []
    );
  }

  public getDraftProjectById(projectId: string): Signal<Project | undefined> {
    return computed(() => this.draftProjects.get(projectId)());
  }

  public loadCompanyArchivedProjects(companyId: string): void {
    if (this.archivedCompanyProjects.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyArchivedProjects(companyId);
  }

  public loadCompanyDraftProjects(companyId: string): void {
    if (this.draftCompanyProjects.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyDraftProjects(companyId);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected handleEvent(_event: AppEvent): void {}

  public loadCompanyProjects(companyId: string): void {
    if (this.companyProjects.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyProjects(companyId);
  }

  public reloadCompanyProjects(companyId: string): void {
    this.fetchCompanyProjects(companyId);
  }

  public upsertLocalData(dto: ProjectDTO): Signal<Project> {
    return dto ? this.projects.upsert(this.projectsFactory.produce(dto)) : signal(undefined);
  }

  public upsertArchiveLocalData(dto: ProjectDTO): Signal<Project> {
    return dto ? this.archivedProjects.upsert(this.projectsFactory.produce(dto)) : signal(undefined);
  }

  public upsertDraftLocalData(dto: ProjectDTO): Signal<Project> {
    return dto ? this.draftProjects.upsert(this.projectsFactory.produce(dto)) : signal(undefined);
  }

  private fetchCompanyProjects(companyId: string): void {
    this.loaderService.startLoading();

    this.httpClient.get<ProjectDTO[]>(`${environment.APIEndPoint}companies/${companyId}/projects`).subscribe({
      next: (projects: ProjectDTO[]) => {
        this.projects.upsertMany(projects.map(itm => this.projectsFactory.produce(itm)));

        this.companyProjects.upsert({
          id: companyId,
          ids: projects.map(itm => itm.id)
        });

        this.loaderService.stopLoading();
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public createProject(project: CreateProjectDTO): Observable<ProjectDTO> {
    const companyId = this.activeCompanyService.activeCompanyId();
    if (!companyId) {
      throw new Error('Active company is required to create a project');
    }
    return this.httpClient.post<ProjectDTO>(`${environment.APIEndPoint}companies/${companyId}/projects`, project).pipe(
      tap({
        next: (createdProject: ProjectDTO) => {
          this.toastService.success(
            this.translateService.instant('company-projects.toasts.successfully-created', {
              name: createdProject.name
            })
          );
          this.upsertLocalData(createdProject);
          const existingActiveProject: string[] = this.companyProjects.get(companyId)().ids ?? [];
          this.companyProjects.upsert({
            id: companyId,
            ids: existingActiveProject.concat(createdProject.id)
          });
        }
      })
    );
  }

  public updateProject(project: UpdateProjectDTO, isDraftList: boolean): Observable<ProjectDTO> {
    this.loaderService.startLoading();
    return this.httpClient
      .put<ProjectDTO>(
        `${environment.APIEndPoint}${isDraftList ? 'project-drafts' : 'projects'}/${project.id}`,
        project
      )
      .pipe(
        tap({
          next: (project: ProjectDTO) => {
            this.toastService.success(
              this.translateService.instant('company-projects.toasts.successfully-created', {
                name: project.name
              })
            );
            if (isDraftList) {
              this.upsertDraftLocalData(project);
            } else {
              this.upsertLocalData(project);
            }
          },
          error: () => this.loaderService.stopLoading(),
          complete: () => this.loaderService.stopLoading()
        })
      );
  }

  private fetchCompanyArchivedProjects(companyId: string): void {
    this.loaderService.startLoading();
    this.httpClient.get<ProjectDTO[]>(`${environment.APIEndPoint}companies/${companyId}/archived-projects`).subscribe({
      next: (projects: ProjectDTO[]) => {
        this.archivedProjects.upsertMany(projects.map(itm => this.projectsFactory.produce(itm)));

        this.archivedCompanyProjects.upsert({
          id: companyId,
          ids: projects.map(itm => itm.id)
        });

        this.loaderService.stopLoading();
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  private fetchCompanyDraftProjects(companyId: string): void {
    this.loaderService.startLoading();

    this.httpClient.get<ProjectDTO[]>(`${environment.APIEndPoint}companies/${companyId}/project-drafts`).subscribe({
      next: (projects: ProjectDTO[]) => {
        this.draftProjects.upsertMany(projects.map(itm => this.projectsFactory.produce(itm)));

        this.draftCompanyProjects.upsert({
          id: companyId,
          ids: projects.map(itm => itm.id)
        });

        this.loaderService.stopLoading();
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public archiveProject(project: Project, companyId: string): void {
    this.loaderService.startLoading();
    this.httpClient.put<ProjectDTO>(`${environment.APIEndPoint}projects/${project.id}/archive`, {}).subscribe({
      next: (project: ProjectDTO) => {
        this.upsertArchiveLocalData(project);
        const existingArchivedProject: string[] = this.archivedCompanyProjects.get(companyId)().ids ?? [];
        const existingActiveProject: string[] = this.companyProjects.get(companyId)().ids ?? [];
        this.archivedCompanyProjects.upsert({
          id: companyId,
          ids: existingArchivedProject.concat(project.id)
        });
        this.companyProjects.upsert({
          id: companyId,
          ids: existingActiveProject.filter(projectId => projectId !== project.id)
        });
        this.toastService.success(
          this.translateService.instant('company-projects.toasts.successfully-archived', {
            name: project.name
          })
        );
        this.loaderService.stopLoading();
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public restoreProject(project: Project, companyId: string): void {
    this.loaderService.startLoading();
    this.httpClient.put<ProjectDTO>(`${environment.APIEndPoint}archived-projects/${project.id}/restore`, {}).subscribe({
      next: (project: ProjectDTO) => {
        this.upsertLocalData(project);
        const existingArchivedProject: string[] = this.archivedCompanyProjects.get(companyId)().ids ?? [];
        const existingActiveProject: string[] = this.companyProjects.get(companyId)().ids ?? [];
        this.companyProjects.upsert({
          id: companyId,
          ids: existingActiveProject.concat(project.id)
        });
        this.archivedCompanyProjects.upsert({
          id: companyId,
          ids: existingArchivedProject.filter(projectId => projectId !== project.id)
        });
        this.toastService.success(
          this.translateService.instant('company-projects.toasts.successfully-restored', {
            name: project.name
          })
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }

  public deleteProject(project: Project, companyId: string, isDraftList = false): void {
    this.loaderService.startLoading();
    this.httpClient.delete<ProjectDTO>(`${environment.APIEndPoint}archived-projects/${project.id}`, {}).subscribe({
      next: () => {
        if (isDraftList) {
          const existingDraftProject: string[] = this.draftCompanyProjects.get(companyId)().ids ?? [];
          this.draftCompanyProjects.upsert({
            id: companyId,
            ids: existingDraftProject.filter(projectId => projectId !== project.id)
          });
        } else {
          const existingArchivedProject: string[] = this.archivedCompanyProjects.get(companyId)().ids ?? [];
          this.archivedCompanyProjects.upsert({
            id: companyId,
            ids: existingArchivedProject.filter(projectId => projectId !== project.id)
          });
        }
        this.toastService.success(
          this.translateService.instant('company-projects.toasts.successfully-deleted', {
            name: project.name
          })
        );
      },
      error: () => this.loaderService.stopLoading(),
      complete: () => this.loaderService.stopLoading()
    });
  }
}
